/**
 * useSyncSettings — listens to `window.storage` events for cross-tab sync.
 * Returns current stored value + setter that writes to localStorage + triggers sync.
 *
 * @param key - localStorage key to watch
 * @param defaultValue - fallback if key not present
 *
 * Flow: storage event fires → hook detects external change → returns new value.
 * The setter writes to localStorage, which fires storage events in OTHER tabs.
 */
import { useState, useEffect, useCallback } from "react";

export function useSyncSettings<T>(key: string, defaultValue: T) {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      // Booleans stored as JSON; strings stored bare (i18next format compatibility)
      if (typeof defaultValue === "boolean") return JSON.parse(stored) as T;
      return stored as unknown as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== key) return;
      if (e.newValue === null) {
        setValue(defaultValue);
        return;
      }
      try {
        setValue(JSON.parse(e.newValue) as T);
      } catch {
        setValue(e.newValue as unknown as T);
      }
    };

    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [key, defaultValue]);

  /** Write to localStorage. Fires storage event in all OTHER tabs. */
  const setStoredValue = useCallback(
    (newValue: T) => {
      const isString = typeof newValue === "string";
      // i18next stores language as bare string ("en"), not JSON ("\"en\"")
      const stored = isString ? (newValue as unknown as string) : JSON.stringify(newValue);
      localStorage.setItem(key, stored);
      setValue(newValue);
      // Manually dispatch a storage event for the current tab
      // so listeners (like i18n) can react immediately.
      window.dispatchEvent(
        new StorageEvent("storage", { key, newValue: stored })
      );
    },
    [key],
  );

  return [value, setStoredValue] as const;
}